/**
 * PostHog experiment registry for CMMC landing pages.
 *
 * One feature flag per experiment, keyed `cmmc-lp-<page>-<test>`. Pages
 * listed here load PostHog eagerly so the variant resolves before the
 * tested element paints; every other page keeps the lazy-load.
 */

export interface Experiment {
  /** PostHog feature-flag key (kebab-case, one flag per experiment). */
  flagKey: string
  /** Marketing-doc uids the experiment runs on. */
  uids: readonly string[]
  /**
   * Code-keyed hero H1 copy per variant (Prismic multivariate isn't set up).
   * A variant with no entry renders the Prismic document copy, so an empty
   * map is a safe dormant state.
   */
  h1Overrides?: Readonly<Record<string, string>>
}

export const EXPERIMENTS: readonly Experiment[] = [
  {
    // Experiment 1: hero H1 message-match on the services cluster.
    // Variant copy is set with marketing before the flag is created in
    // PostHog; until then the flag doesn't exist and every visitor gets
    // the Prismic (control) copy.
    flagKey: 'cmmc-lp-services-h1-message-match',
    uids: ['cmmc-compliance-services'],
    h1Overrides: {},
  },
]

/**
 * Max time the tested element stays hidden waiting for the variant. Past
 * this, control ships for the pageview — a later swap would repaint
 * mid-read.
 */
export const EXPERIMENT_RESOLVE_TIMEOUT_MS = 300

export function getExperimentForUid(uid: string | undefined): Experiment | undefined {
  if (!uid) return undefined
  return EXPERIMENTS.find((experiment) => experiment.uids.includes(uid))
}

/**
 * True when the pathname points at an experiment page. Marketing pages live
 * at `/:uid` with an optional locale prefix, so the last segment is the uid.
 */
export function pathHasExperiment(pathname: string): boolean {
  const lastSegment = pathname.split('/').filter(Boolean).pop()
  if (!lastSegment) return false
  return EXPERIMENTS.some((experiment) => experiment.uids.includes(lastSegment))
}
